import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { fetchEvents } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import EventCard from "@/components/EventCard";

const CITIES = ["All Cities", "Houston", "Atlanta", "Dallas", "Los Angeles", "New Orleans", "New York", "San Diego", "Golden Triangle"];
const SOURCES = [
  { key: "all", label: "All Events" },
  { key: "editorial", label: "Featured" },
  { key: "community", label: "Community" },
];

export default function Events() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [city, setCity] = useState("All Cities");
  const [source, setSource] = useState("all");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEvents().then(setEvents).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return events.filter((e) =>
      (city === "All Cities" || e.city === city) &&
      (source === "all" || (e.source || "editorial") === source) &&
      (!q || [e.name, e.host_name, e.category, e.short_description, e.venue, e.city]
        .filter(Boolean).some((v) => v.toLowerCase().includes(q)))
    );
  }, [events, city, source, query]);

  return (
    <div data-testid="events-page" className="bg-sand-50">
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div>
            <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-brand-500 mb-4">
              The Calendar · 2026
            </div>
            <h1 className="font-serif font-black text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-sand-900 max-w-4xl">
              Festivals, tastings<br />& the country's<br />biggest tables.
            </h1>
            <p className="mt-6 max-w-2xl text-lg font-light text-sand-700 leading-relaxed">
              Signature Tastebuds events plus community pop-ups, dinners, and tastings
              hosted by restaurants, chefs, food trucks, and planners across the network.
            </p>
          </div>
          <Link
            to={user ? "/events/new" : "/login"}
            data-testid="host-event-cta"
            className="shrink-0 inline-flex items-center gap-2 bg-brand-500 text-sand-50 px-7 py-4 font-mono text-[11px] font-bold tracking-[0.25em] uppercase hover:bg-brand-600 transition-colors active:scale-95 self-start"
          >
            <Plus size={15} /> Host an Event
          </Link>
        </div>

        <div className="mt-12 space-y-6">
          <div className="relative max-w-xl">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-sand-500" />
            <input
              data-testid="event-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search events, hosts, categories…"
              className="w-full border border-sand-300 pl-11 pr-4 py-3 bg-white focus:border-brand-500 outline-none font-light"
            />
          </div>
          <div>
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-sand-700 mb-3">Type</div>
            <div className="flex flex-wrap gap-2.5" data-testid="event-source-filters">
              {SOURCES.map((s) => (
                <button
                  key={s.key}
                  data-testid={`event-source-${s.key}`}
                  onClick={() => setSource(s.key)}
                  className={`px-5 py-2.5 font-mono text-[10px] tracking-[0.2em] uppercase border transition-all active:scale-95 ${
                    source === s.key ? "bg-sand-900 text-sand-50 border-sand-900" : "bg-transparent text-sand-900 border-sand-300 hover:border-brand-500"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-sand-700 mb-3">City</div>
            <div className="flex flex-wrap gap-2.5" data-testid="event-city-filters">
              {CITIES.map((c) => (
                <button
                  key={c}
                  data-testid={`event-city-${c.toLowerCase().replace(/\s/g, "-")}`}
                  onClick={() => setCity(c)}
                  className={`px-5 py-2.5 font-mono text-[10px] tracking-[0.2em] uppercase border transition-all active:scale-95 ${
                    city === c
                      ? "bg-brand-500 text-sand-50 border-brand-500"
                      : "bg-transparent text-sand-900 border-sand-300 hover:border-brand-500"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-32">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => <div key={i} className="h-96 bg-sand-100 animate-pulse" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center">
            <p className="font-serif italic text-2xl text-sand-700">No events here yet.</p>
            <Link to={user ? "/events/new" : "/login"} className="mt-4 inline-block font-mono text-[11px] tracking-[0.25em] uppercase text-brand-500 editorial-link">
              Be the first to host one →
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map((e, i) => <EventCard key={e.id} e={e} index={i} />)}
          </div>
        )}
      </section>
    </div>
  );
}
